/**
 * The rule behind the two numeric boxes of a duration range — what the
 * operator typed, turned into `{min, max}` or a refusal.
 *
 * Pure, no React, so it runs under `node --test`. Used by
 * `@components/DurationRangeField` when it commits a draft (on blur or
 * Enter); the per-keystroke mode of Price limits never calls it, since a
 * half-typed number must not be swapped or refused.
 */

/** `''`/`null` → `null`; a whole number → the number; anything else → `NaN`. */
function parseSide(v) {
	if (v == null) return null
	const t = String(v).trim()
	if (t === '') return null
	return /^-?\d+$/.test(t) ? Number(t) : NaN
}

/**
 * `{ value, error }`: `value` is `{min, max}` with `null` for an empty
 * side, or `null` when both are empty; `error` is `null`, or the side
 * that is not a whole number inside `bounds` — `'min'`, `'max'` or
 * `'both'` — in which case `value` is `null` and nothing should be sent.
 * A second value below the first is swapped, never an error (D3, like
 * `rangeFromPicker` does with dates).
 */
export function normaliseDuration({ min, max } = {}, { min: lo = -Infinity, max: hi = Infinity } = {}) {
	let a = parseSide(min), b = parseSide(max)
	const bad = (n) => n != null && (Number.isNaN(n) || n < lo || n > hi)
	const badMin = bad(a), badMax = bad(b)
	if (badMin || badMax) {
		return { value: null, error: badMin && badMax ? 'both' : badMin ? 'min' : 'max' }
	}
	if (a == null && b == null) return { value: null, error: null }
	if (a != null && b != null && a > b) [a, b] = [b, a]
	return { value: { min: a, max: b }, error: null }
}
